/*
 * Hyper Sunk motion system
 * ------------------------------------------------------------------
 * Scroll-reveal, hero entrance and number count-up for the home and
 * flagship pages.
 *
 * Safety model: nothing is hidden by CSS on its own. The reveal styles are
 * scoped to `html.hs-motion`, and this script is the only thing that adds
 * that class. If the script fails to load, is blocked, or the visitor has
 * asked for reduced motion, the class never lands and every section renders
 * plainly visible. Animation is therefore purely additive.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  // Bail out early for reduced-motion users and browsers without IO.
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) return;

  root.classList.add('hs-motion');

  /* ----------------------------------------------------------------
     1. Scroll reveal
     Elements are grouped so siblings stagger against each other rather
     than every element on the page sharing one global counter.
     ---------------------------------------------------------------- */
  var GROUPS = [
    '.hs-section-header',
    '.hs-story-card',
    '.hs-story-badge-item',
    '.hs-trust-bar-item',
    '.hs-trustbar-item',
    '.hs-product-card',
    '.hs-how-step',
    '.hs-timeline-node',
    '.hs-ba-pane',
    '.hs-ba-card',
    '.hs-cert-box',
    '.hs-review-card',
    '.hs-faq-item',
    '.hs-range-device',
    '.hs-range-form-card',
    '.hs-spotlight-media',
    '.hs-spotlight-copy',
    '.hs-final-cta-box',
    '.hs-compare-scroll',
    '.hs-cryo-point-box',
    '.hs-transform-card',
    '.hs-unboxing-card',
    '.hs-level-tile',
    '.hs-timeline-card',
    '.hs-semi-metric-card',
    '.hs-zone-card',
    '.hs-reel-card',
    '.hs-pdp-spec-grid article'
  ];

  var STAGGER = 70;    // ms between siblings
  var MAX_STAGGER = 6; // stop stacking delay after this many siblings

  var targets = [];
  GROUPS.forEach(function (selector) {
    var found = document.querySelectorAll(selector);
    if (!found.length) return;

    // Reset the stagger counter per parent so each row starts from zero.
    var seenParents = new Map();
    Array.prototype.forEach.call(found, function (el) {
      if (el.hasAttribute('data-hs-reveal')) return;
      var parent = el.parentNode;
      var index = seenParents.get(parent) || 0;
      seenParents.set(parent, index + 1);

      el.setAttribute('data-hs-reveal', '');
      el.style.transitionDelay = (Math.min(index, MAX_STAGGER) * STAGGER) + 'ms';
      targets.push(el);
    });
  });

  var revealObserver = new IntersectionObserver(function (entries, obs) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('hs-in');
      obs.unobserve(entry.target); // one-shot; never animates back out
    });
  }, {
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.08
  });

  targets.forEach(function (el) { revealObserver.observe(el); });

  /* ----------------------------------------------------------------
     2. Hero entrance
     The hero is above the fold, so it plays on load rather than waiting
     for a scroll event that may never come.
     ---------------------------------------------------------------- */
  function playHero() {
    var heroes = document.querySelectorAll('.hs-hero-section');
    Array.prototype.forEach.call(heroes, function (hero) {
      var pieces = hero.querySelectorAll(
        '.hs-eyebrow, .hs-hero-title, h1, .hs-hero-lead, .hs-hero-desc,' +
        '.hs-hero-actions, .hs-hero-cta-group, .hs-hero-trust-row,' +
        '.hs-hero-specs-grid, .hs-hero-metrics, .hs-hero-stage-col, .hs-hero-stage'
      );
      Array.prototype.forEach.call(pieces, function (el, i) {
        el.setAttribute('data-hs-hero', '');
        el.style.transitionDelay = (i * 80) + 'ms';
      });
      // Next frame, so the initial hidden state is painted first.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          Array.prototype.forEach.call(pieces, function (el) { el.classList.add('hs-in'); });
        });
      });
    });
  }

  /* ----------------------------------------------------------------
     3. Number count-up
     Runs once when a stat scrolls into view. Any non-numeric decoration
     around the figure (-10°C, 8.4k+, 999K) is preserved by capturing the
     prefix and suffix and only animating the number itself.
     ---------------------------------------------------------------- */
  function countUp(el) {
    var raw = el.textContent.trim();
    var match = raw.match(/^(\D*?)(\d[\d,]*\.?\d*)(.*)$/);
    if (!match) return;

    var prefix = match[1];
    var suffix = match[3];
    var numText = match[2].replace(/,/g, '');
    var target = parseFloat(numText);
    if (!isFinite(target) || target === 0) return;

    var decimals = (numText.split('.')[1] || '').length;
    var grouped = match[2].indexOf(',') !== -1;
    var duration = 1100;
    var start = null;

    function format(value) {
      var out = value.toFixed(decimals);
      if (grouped) out = Number(out).toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
      return prefix + out + suffix;
    }

    function frame(now) {
      if (start === null) start = now;
      var progress = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      el.textContent = format(target * eased);
      if (progress < 1) requestAnimationFrame(frame);
      else el.textContent = raw; // restore the exact original string
    }

    requestAnimationFrame(frame);
  }

  var counters = document.querySelectorAll('.hs-spec-num, .hs-hero-metric-num, .hs-rating-number');
  if (counters.length) {
    var countObserver = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        countUp(entry.target);
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.6 });
    Array.prototype.forEach.call(counters, function (el) { countObserver.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', playHero);
  } else {
    playHero();
  }
})();
